import styled from '@emotion/styled'
import {
    ApiShopperLandingPageLink,
    REVIEWS_CAROUSEL_LIMITS,
} from '@storefronts/common-types'
import { useContext } from 'react'
import {
    PRIMARY_FONT_CSS_VAR,
    SECONDARY_FONT_CSS_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_BORDER_COLOR_VAR,
    CustomizationContext,
} from '../../../../context/CustomizationContextProvider'
import { ReviewStar } from '../../reviews/ReviewStar'
import {
    pageBleedTrack,
    scaledFontSize,
} from '../../../../style/scrollingLayout'
import { Constants } from '../../../../style/styleConstants'
import { DEFAULT_LOCALE } from '../../../../constants'
import { getTypographyRoleStyle } from '../../../../style/typography'
import { SectionContainer, SectionTitle } from '../Shared'
import { useLandingPageLinksForHashes } from '../../../../hooks/useLandingPageLinksForHashes'
import { ReviewSlide } from './ReviewSlide'
import { ReviewsCarouselSectionProps, ReviewTheme } from './types'

const SLIDE_GAP = 12

// This section renders full-bleed (InlineWrapper, no SectionWrapper padding),
// so inner rows re-apply the content margin to align with other sections while
// the scroll track runs edge-to-edge.
const HORIZONTAL_PADDING = {
    paddingLeft: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
    paddingRight: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
}

const Header = styled.div({
    ...HORIZONTAL_PADDING,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
})

const Average = styled.div({
    ...getTypographyRoleStyle('displayHeading', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: '-0.5px',
    }),
    fontSize: scaledFontSize(36),
})

const HeaderMeta = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
})

const HeaderLabel = styled.div({
    ...getTypographyRoleStyle('badgeText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
    }),
    fontSize: scaledFontSize(12),
})

const Track = styled.div({
    ...HORIZONTAL_PADDING,
    display: 'flex',
    // Stretch so every card matches the tallest card's height.
    alignItems: 'stretch',
    gap: SLIDE_GAP,
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    scrollPaddingLeft: Constants.CONTENT_BLOCK_MOBILE_MARGIN,
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    overscrollBehaviorX: 'contain',
    paddingBottom: 2,
    '&::-webkit-scrollbar': { display: 'none' },
    ...pageBleedTrack,
})

interface ReviewsCarouselProps {
    section: ReviewsCarouselSectionProps
}

// Mid-generation previews can render the section before its reviews are
// attached; an absent array must read as empty, not crash the page.
function visibleReviews(
    section: ReviewsCarouselSectionProps,
): ReviewsCarouselSectionProps['reviews'] {
    return section.reviews ?? []
}

function linkedProductReviewStats(
    linkHash: string | undefined,
    resolvedLinks: ApiShopperLandingPageLink[],
): { rating: number | null; count: number | null } {
    if (!linkHash) {
        return { rating: null, count: null }
    }
    const productReviews = resolvedLinks[0]?.reviews
    const rating = productReviews?.rating ?? null
    return {
        rating,
        count: rating != null ? (productReviews?.count ?? null) : null,
    }
}

export const ReviewsCarousel: React.FC<ReviewsCarouselProps> = ({
    section,
}) => {
    const { accentFillColor, accentTextColor } =
        useContext(CustomizationContext)
    const resolvedLinks = useLandingPageLinksForHashes(
        section.linkHash ? [section.linkHash] : [],
    )

    const reviews = visibleReviews(section).slice(
        0,
        REVIEWS_CAROUSEL_LIMITS.MAX_REVIEWS,
    )
    const count = reviews.length

    if (count === 0) return null

    const onAccentBlock = Boolean(section.backgroundColor)
    const pageText = `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
    const pageFill = `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`

    // The configured background color lives on the cards; the section sits on a
    // lighter tint of that same color.
    const cardText =
        section.textColor ?? (onAccentBlock ? accentTextColor : pageText)
    const theme: ReviewTheme = {
        accent: onAccentBlock ? cardText : accentFillColor,
        baseText: cardText,
        cardSurface: section.backgroundColor ?? pageFill,
        cardBorder: onAccentBlock
            ? `1px solid color-mix(in srgb, ${cardText} 16%, transparent)`
            : `1px solid var(${SUBTLE_BORDER_COLOR_VAR})`,
        mutedText: `color-mix(in srgb, ${cardText} 60%, transparent)`,
    }

    const sectionBg = section.backgroundColor
        ? `color-mix(in srgb, ${section.backgroundColor} 14%, ${pageFill})`
        : undefined
    const sectionMuted = `color-mix(in srgb, ${pageText} 60%, transparent)`

    // Use the selected product's real aggregate only when a product is linked
    // and has a real rating; otherwise fall back wholly to the inline average.
    // The rating drives the count so the stars and the count label never mix
    // product and inline numbers.
    const { rating: productRating, count: reviewCount } =
        linkedProductReviewStats(section.linkHash, resolvedLinks)
    const averageRating =
        productRating ??
        reviews.reduce((sum, review) => sum + review.rating, 0) / count

    return (
        <SectionContainer
            verticalPadding={onAccentBlock ? 24 : undefined}
            style={{
                background: sectionBg,
                color: pageText,
            }}
        >
            {section.sectionTitle ? (
                <SectionTitle isHorizontalScroll>
                    {section.sectionTitle}
                </SectionTitle>
            ) : null}

            <Header>
                <Average>{averageRating.toFixed(1)}</Average>
                <HeaderMeta>
                    <ReviewStar
                        rating={averageRating}
                        fillColor={accentFillColor}
                        starWidth={16}
                    />
                    <HeaderLabel style={{ color: sectionMuted }}>
                        {reviewCount != null
                            ? `${reviewCount.toLocaleString(DEFAULT_LOCALE)} Verified Reviews`
                            : 'Verified Reviews'}
                    </HeaderLabel>
                </HeaderMeta>
            </Header>

            <Track
                role="group"
                aria-roledescription="carousel"
                aria-label={section.sectionTitle || 'Customer reviews'}
            >
                {reviews.map((review, i) => (
                    <ReviewSlide
                        key={i}
                        review={review}
                        index={i}
                        count={count}
                        theme={theme}
                    />
                ))}
            </Track>
        </SectionContainer>
    )
}
